import { HOURS_PER_DAY } from '../../constants';
import type { Assignment, DateIso, Dev, Phase } from '../../types/domain';
import { dayCoverage } from '../absences';
import { addDays, dateFromIso, getHolidaysCached, isoStr, isWE } from '../dates';
import { toNumber } from '../numbers';
import { phaseBrickId } from '../phases';
import type { DevCalendar } from './types';

// Capacité max/jour d'un dev pour une affectation : jour plein si des jours travaillés précis sont
// définis (le temps partiel est alors géré par l'absence de saisie ces jours-là, pas par un plafond
// d'heures), sinon le taux de contrat réduit la capacité générique (ex. 80% → 5,6h/j).
export function maxHoursPerDay(
  dev: Pick<Dev, 'joursTravailles' | 'tauxContrat'> | null | undefined,
): number {
  const devWorkDays = dev?.joursTravailles || [];
  return devWorkDays.length > 0
    ? HOURS_PER_DAY
    : HOURS_PER_DAY * (Number(dev?.tauxContrat || 100) / 100);
}

export function devWorkingDays(dev: DevCalendar, dateStart: Date, dateEnd: Date): number {
  const jt = dev.joursTravailles || []; // [1=lun … 5=ven], vide = tous les jours
  let c = 0;
  const absences = dev.absences || [];
  let cur = new Date(dateStart);
  while (cur <= dateEnd) {
    const iso = isoStr(cur);
    const jsDay = cur.getDay(); // 0=dim, 1=lun…
    const isoDay = jsDay === 0 ? 7 : jsDay; // 1=lun…7=dim
    const isWorked = jt.length === 0 || jt.includes(isoDay);
    if (!isWE(cur) && !getHolidaysCached(cur.getFullYear()).has(iso) && isWorked) {
      const cov = dayCoverage(absences, iso);
      c += (cov.am ? 0 : 0.5) + (cov.pm ? 0 : 0.5);
    }
    cur = addDays(cur, 1);
  }
  return c;
}

export function devAssignedHoursOnDay(
  affectations: Assignment[],
  devId: string,
  iso: DateIso,
): number {
  let h = 0;
  for (const a of affectations) {
    if (a.devId !== devId) continue;
    const s = dateFromIso(a.debut),
      e = dateFromIso(a.fin),
      d = dateFromIso(iso);
    if (d >= s && d <= e) h += toNumber(a.heuresParJour || 0);
  }
  return h;
}

// devsById : { [devId]: {joursTravailles, absences} } — le vrai temps partiel et les vraies
// absences du dev affecté, pour ne pas compter des jours "planifiés" qu'il ne peut pas travailler.
export function brickAssignedDays(
  affectations: Assignment[],
  bid: string,
  devsById: Record<string, DevCalendar> | null | undefined,
): number {
  return affectations
    .filter((a) => a.briqueId === bid)
    .reduce((s, a) => {
      const dev: DevCalendar = (devsById && devsById[a.devId]) || { absences: [] };
      const nb = devWorkingDays(dev, dateFromIso(a.debut), dateFromIso(a.fin));
      return s + (nb * toNumber(a.heuresParJour || 0)) / HOURS_PER_DAY;
    }, 0);
}

export function phaseAssignedDays(
  affectations: Assignment[],
  phase: Phase,
  devsById: Record<string, DevCalendar> | null | undefined,
): number {
  const phStart = phase.dateDebut || null;
  const phEnd = phase.dateFin || null;
  // Seuls les jours compris dans la phase comptent : une affectation du 15/12 au 15/01 sur une
  // phase qui finit le 31/12 compte du 15/12 au 31/12 (elle reste entière sur la Timeline).
  const inPhase = affectations
    .filter((a) => !(phEnd && a.debut > phEnd) && !(phStart && a.fin < phStart))
    .map((a) => ({
      ...a,
      debut: phStart && a.debut < phStart ? phStart : a.debut,
      fin: phEnd && a.fin > phEnd ? phEnd : a.fin,
    }));
  return (
    (phase.briques || []).reduce((s, b) => s + brickAssignedDays(inPhase, b.id, devsById), 0) +
    brickAssignedDays(inPhase, phaseBrickId(phase.id), devsById)
  );
}
